import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { frankfurterService, ExchangeRates } from '../services/frankfurter.service';
import { CreateExpenseInput } from '../services/supabase/expenses.service';

export interface ConversionResult {
  converted_amount: number;
  converted_currency: string;
  exchange_rate: number;
  rate_date: string;
}

export type ExpenseInputWithoutConversion = Omit<CreateExpenseInput, 'converted_amount' | 'converted_currency' | 'exchange_rate' | 'rate_date'>;

interface ConversionContextType {
  getLatestRates: (baseCurrency: string, targetCurrencies?: string[]) => Promise<ExchangeRates>;
  convertAmount: (amount: number, fromCurrency: string, toCurrency: string, date?: string) => Promise<number>;
  convertExpenseAmount: (amount: number, fromCurrency: string, toCurrency: string, expenseDate: string) => Promise<ConversionResult>;
  prepareExpenseWithConversion: (
    expenseData: ExpenseInputWithoutConversion,
    userPreferredCurrency: string
  ) => Promise<CreateExpenseInput>;
  convertUsingReportRates: (
    amount: number,
    fromCurrency: string,
    reportCurrency: string,
    reportRates: Record<string, number>
  ) => number;
  clearConversionCache: () => void;
}

const ConversionContext = createContext<ConversionContextType | undefined>(undefined);

export const ConversionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const warnedPairsRef = useRef<Set<string>>(new Set());

  const warnOnce = useCallback((key: string, message: string) => {
    if (warnedPairsRef.current.has(key)) return;
    warnedPairsRef.current.add(key);
    console.warn(message);
  }, []);

  const getLatestRates = useCallback(
    async (baseCurrency: string, targetCurrencies?: string[]) => {
      return frankfurterService.getLatestRates(baseCurrency.toUpperCase(), targetCurrencies);
    },
    []
  );

  const convertAmount = useCallback(
    async (amount: number, fromCurrency: string, toCurrency: string, date?: string) => {
      const from = fromCurrency.toUpperCase();
      const to = toCurrency.toUpperCase();

      if (from === to) return amount;

      try {
        return await frankfurterService.convertCurrency(amount, from, to, date);
      } catch (error) {
        warnOnce(`${from}-${to}-convert`, `Exchange rate not found for ${from} or ${to}. Using 1:1 conversion.`);
        return amount;
      }
    },
    [warnOnce]
  );

  const convertExpenseAmount = useCallback(
    async (amount: number, fromCurrency: string, toCurrency: string, expenseDate: string): Promise<ConversionResult> => {
      const from = fromCurrency.toUpperCase();
      const to = toCurrency.toUpperCase();

      if (from === to) {
        return {
          converted_amount: amount,
          converted_currency: to,
          exchange_rate: 1.0,
          rate_date: expenseDate,
        };
      }

      try {
        const rates = await frankfurterService.getHistoricalRates(expenseDate, from, [to]);
        const historicalRate = rates.rates[to];

        if (historicalRate) {
          return {
            converted_amount: amount * historicalRate,
            converted_currency: to,
            exchange_rate: historicalRate,
            rate_date: rates.date,
          };
        }

        const latestRates = await frankfurterService.getLatestRates(from, [to]);
        const latestRate = latestRates.rates[to];

        if (latestRate) {
          return {
            converted_amount: amount * latestRate,
            converted_currency: to,
            exchange_rate: latestRate,
            rate_date: latestRates.date,
          };
        }

        warnOnce(`${from}-${to}-expense`, `Exchange rate not found for ${from} or ${to}. Using 1:1 conversion.`);
        return {
          converted_amount: amount,
          converted_currency: to,
          exchange_rate: 1.0,
          rate_date: expenseDate,
        };
      } catch (error) {
        warnOnce(`${from}-${to}-expense-error`, `Exchange rate not found for ${from} or ${to}. Using 1:1 conversion.`);
        return {
          converted_amount: amount,
          converted_currency: to,
          exchange_rate: 1.0,
          rate_date: expenseDate,
        };
      }
    },
    [warnOnce]
  );

  const prepareExpenseWithConversion = useCallback(
    async (
      expenseData: ExpenseInputWithoutConversion,
      userPreferredCurrency: string
    ): Promise<CreateExpenseInput> => {
      const expenseCurrency = expenseData.currency || 'USD';
      const conversion = await convertExpenseAmount(
        expenseData.amount,
        expenseCurrency,
        userPreferredCurrency,
        expenseData.expense_date
      );

      return {
        ...expenseData,
        converted_amount: conversion.converted_amount,
        converted_currency: conversion.converted_currency,
        exchange_rate: conversion.exchange_rate,
        rate_date: conversion.rate_date,
      };
    },
    [convertExpenseAmount]
  );

  const convertUsingReportRates = useCallback(
    (
      amount: number,
      fromCurrency: string,
      reportCurrency: string,
      reportRates: Record<string, number>
    ): number => {
      const from = fromCurrency.toUpperCase();
      const report = reportCurrency.toUpperCase();

      if (from === report) return amount;
      if (!reportRates || Object.keys(reportRates).length === 0) return amount;

      const fromRate = reportRates[from];
      if (!fromRate) {
        warnOnce(`${from}-${report}-report`, `Exchange rate not found for ${from} or ${report}. Using 1:1 conversion.`);
        return amount;
      }

      return amount / fromRate;
    },
    [warnOnce]
  );

  const clearConversionCache = useCallback(() => {
    frankfurterService.clearCache();
    warnedPairsRef.current.clear();
  }, []);

  const value = useMemo<ConversionContextType>(
    () => ({
      getLatestRates,
      convertAmount,
      convertExpenseAmount,
      prepareExpenseWithConversion,
      convertUsingReportRates,
      clearConversionCache,
    }),
    [
      getLatestRates,
      convertAmount,
      convertExpenseAmount,
      prepareExpenseWithConversion,
      convertUsingReportRates,
      clearConversionCache,
    ]
  );

  return (
    <ConversionContext.Provider value={value}>
      {children}
    </ConversionContext.Provider>
  );
};

export const useConversion = (): ConversionContextType => {
  const context = useContext(ConversionContext);
  if (!context) {
    throw new Error('useConversion must be used within a ConversionProvider');
  }
  return context;
};
